import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, UploadCloud, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { productsService } from "../../../services/products.service";
import { env } from "../../../config/env";
import { getAuthToken } from "../../AdminAuth/components/authStore";
import CatalogManagerDrawer from "./CatalogManagerDrawer";

function Label({ children }) {
  return (
    <div className="mb-1 text-[12px] font-medium text-slate-500">
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={
        "h-[44px] w-full rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
        className
      }
    />
  );
}

function normalizeSelectOptions(options = []) {
  return (options || [])
    .map((option) => {
      const nextValue = typeof option === "string" ? option : option.value;
      const nextLabel = typeof option === "string" ? option : option.label;

      return {
        value: String(nextValue ?? ""),
        label: String(nextLabel ?? nextValue ?? ""),
      };
    })
    .filter((item) => item.value);
}

function QuickSelectInput({
  value,
  onChange,
  placeholder,
  options = [],
  className = "",
}) {
  const normalizedOptions = normalizeSelectOptions(options);

  return (
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={
          "h-[44px] w-full appearance-none rounded-[8px] border border-[#f2c6cc] bg-white px-4 pr-10 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
          className
        }
      >
        <option value="">{placeholder}</option>

        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
    </div>
  );
}

function FreeTextSelectInput({
  value,
  onChange,
  placeholder,
  options = [],
  listId,
  className = "",
}) {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const normalizedOptions = useMemo(
    () => normalizeSelectOptions(options),
    [options],
  );
  const currentValue = String(value || "");
  const searchValue = currentValue.trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!searchValue) return normalizedOptions;

    return normalizedOptions.filter((option) => {
      const optionValue = String(option.value || "").toLowerCase();
      const optionLabel = String(option.label || "").toLowerCase();
      return optionValue.includes(searchValue) || optionLabel.includes(searchValue);
    });
  }, [normalizedOptions, searchValue]);

  const hasExactMatch = normalizedOptions.some(
    (option) => String(option.value || "").toLowerCase() === searchValue,
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectValue = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={currentValue}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setIsOpen(false);
        }}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        className={
          "h-[44px] w-full rounded-[8px] border border-[#f2c6cc] bg-white px-4 pr-10 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
          className
        }
      />

      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setIsOpen((prev) => !prev)}
        className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-[#d86a78]"
      >
        <ChevronDown size={16} />
      </button>

      {isOpen ? (
        <div
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-[120] mt-1 max-h-[190px] overflow-y-auto rounded-[10px] border border-[#f2c6cc] bg-white p-1 shadow-[0_16px_34px_rgba(15,23,42,0.14)]"
        >
          {currentValue.trim() && !hasExactMatch ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectValue(currentValue.trim())}
              className="mb-1 w-full rounded-[8px] border border-rose-100 bg-rose-50 px-3 py-2 text-left text-[12px] font-medium text-[#d86a78] hover:bg-rose-100"
            >
              Use custom: {currentValue.trim()}
            </button>
          ) : null}

          {filteredOptions.length ? (
            filteredOptions.map((option) => {
              const isSelected = currentValue === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectValue(option.value)}
                  className={
                    "block w-full rounded-[8px] px-3 py-2 text-left text-[12px] transition " +
                    (isSelected
                      ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                      : "text-slate-600 hover:bg-rose-50 hover:text-[#d86a78]")
                  }
                >
                  {option.label}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-[12px] text-slate-400">
              No matching option. Type your custom value.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={
        "min-h-[100px] w-full rounded-[8px] border border-[#f2c6cc] bg-white px-4 py-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
        className
      }
    />
  );
}

function SelectLike({ value, onChange, placeholder, options = [] }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-[44px] w-full appearance-none rounded-[8px] border border-[#f2c6cc] bg-white px-4 pr-10 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={
          "relative h-[22px] w-[46px] rounded-full transition " +
          (value ? "bg-[#d86a78]" : "bg-slate-300")
        }
      >
        <span
          className={
            "absolute top-[3px] h-[16px] w-[16px] rounded-full bg-white shadow transition " +
            (value ? "left-[27px]" : "left-[3px]")
          }
        />
      </button>
      <div
        className={
          "text-[12px] font-semibold " +
          (value ? "text-emerald-600" : "text-rose-600")
        }
      >
        {value ? "Yes" : "No"}
      </div>
    </div>
  );
}

function toSlug(s) {
  const out = String(s || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return out || "unknown";
}

function safeLower(x) {
  return String(x || "")
    .trim()
    .toLowerCase();
}

function normalizeCategory(item = {}) {
  return {
    ...item,
    parent_id: item?.parent_id ?? item?.parentId ?? null,
    parentId: item?.parentId ?? item?.parent_id ?? null,
    image_url: item?.image_url ?? item?.imageUrl ?? "",
    imageUrl: item?.imageUrl ?? item?.image_url ?? "",
  };
}

function buildCategoryPathOptions(items = []) {
  const normalized = (items || []).map(normalizeCategory);
  const byId = new Map(normalized.map((item) => [String(item.id), item]));
  const childCount = new Map();
  for (const item of normalized) {
    if (!item.parentId) continue;
    const key = String(item.parentId);
    childCount.set(key, Number(childCount.get(key) || 0) + 1);
  }

  const pathFor = (item) => {
    const path = [];
    const seen = new Set();
    let current = item;
    while (current?.id && !seen.has(String(current.id))) {
      seen.add(String(current.id));
      path.unshift(current);
      current = current.parentId ? byId.get(String(current.parentId)) : null;
    }
    return path;
  };

  return normalized
    .map((item) => {
      const path = pathFor(item);
      const inferredChildren = Number(childCount.get(String(item.id)) || 0);
      const hasChildren = Boolean(item?.hasChildren ?? item?.has_children ?? inferredChildren > 0);
      return {
        value: String(item.id || ""),
        label: path.map((node) => node.name).filter(Boolean).join(" > "),
        item,
        path,
        isLeaf: !hasChildren,
      };
    })
    .filter((option) => option.value && option.label)
    .sort((a, b) => a.label.localeCompare(b.label));
}

function parseQuantity(qtyRaw) {
  const qty = String(qtyRaw || "").trim();
  if (!qty) return { value: "", unit: "" };
  const m = qty.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*([a-zA-Z]+)\s*$/);
  if (!m) return { value: "", unit: "" };
  const value = m[1];
  const unit = m[2].toLowerCase();
  const norm =
    unit === "l"
      ? "L"
      : unit === "ml"
        ? "ml"
        : unit === "kg"
          ? "kg"
          : unit === "g"
            ? "g"
            : unit === "pcs"
              ? "pcs"
              : unit;
  return { value, unit: norm };
}

function normalizeList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.items)) return json.items;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json?.result)) return json.result;
  return [];
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base =
    env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function cleanNumberForInput(value) {
  if (value === null || value === undefined || value === "") return "";

  const text = String(value).trim();
  if (!text) return "";

  // Only clean valid decimal numbers
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;

  return text
    .replace(/(\.\d*?[1-9])0+$/, "$1") // 750.5000 -> 750.5
    .replace(/\.0+$/, ""); // 750.000 -> 750
}

function createImagePreview(file) {
  return {
    file,
    name: file.name,
    preview: URL.createObjectURL(file),
  };
}

const TAX_PERCENT_OPTIONS = ["0", "5", "12", "18", "28"];

const VARIANT_GROUP_OPTIONS = [
  "size",
  "flavor",
  "color",
  "pack",
  "weight",
  "volume",
  "combo",
  "material",
  "type",
  "fragrance",
  "capacity",
];

const PACK_LABEL_OPTIONS = [
  "Pack",
  "Packet",
  "Pouch",
  "Bottle",
  "Can",
  "Box",
  "Jar",
  "Tray",
  "Bag",
  "Tin",
  "Combo Pack",
  "Pack of 2",
  "Pack of 3",
  "Pack of 6",
  "Pack of 12",
];

const QTY_PACK_UNIT_OPTIONS = [
  "pcs",
  "piece",
  "pack",
  "packet",
  "box",
  "bottle",
  "can",
  "pouch",
  "jar",
  "tray",
  "bag",
  "g",
  "kg",
  "ml",
  "L",
  "dozen",
  "pair",
  "bundle",
];

const WEIGHT_UNIT_OPTIONS = ["g", "kg", "mg", "ml", "L"];

function createEmptyVariant(index = 0) {
  return {
    id: "",
    variantName: "",
    variantSku: "",
    barcode: "",
    packLabel: "",
    unitValue: "",
    unitSymbol: "",
    weightValue: "",
    weightUnitSymbol: "",
    variantGroup: "",
    variantValue: "",
    shortDescription: "",
    description: "",
    mrp: "",
    sellingPrice: "",
    isDefault: index === 0,
    isActive: true,
    sortOrder: index,
    existingImages: [],
    newImages: [],
    coverSelection: { type: "none", value: null },
  };
}

function normalizeIncomingVariant(variant = {}, index = 0) {
  const images = Array.isArray(variant?.images)
    ? variant.images.map((img, idx) => ({
      id: img?.id || `${variant?.id || index}-${idx}`,
      url: resolveImageUrl(img?.url || ""),
      rawUrl: img?.url || "",
      isPrimary: Boolean(img?.isPrimary ?? img?.is_primary ?? idx === 0),
    }))
    : [];
  const cover =
    images.find((img) => img.isPrimary)?.id || images[0]?.id || null;
  return {
    id: variant?.id || "",
    variantName: variant?.variantName || variant?.variant_name || "",
    variantSku:
      variant?.variantSku || variant?.variant_sku || variant?.sku || "",
    barcode: variant?.barcode || "",
    packLabel: variant?.packLabel || variant?.pack_label || "",
    unitValue: cleanNumberForInput(variant?.unitValue ?? variant?.unit_value),
    unitSymbol: variant?.unitSymbol || variant?.unit_symbol || "",
    weightValue: cleanNumberForInput(variant?.weightValue ?? variant?.weight_value),
    weightUnitSymbol:
      variant?.weightUnitSymbol || variant?.weight_unit_symbol || "",
    variantGroup: variant?.variantGroup || variant?.variant_group || "",
    variantValue: variant?.variantValue || variant?.variant_value || "",
    shortDescription:
      variant?.shortDescription || variant?.short_description || "",
    description: variant?.description || "",
    mrp: cleanNumberForInput(variant?.mrp),
    sellingPrice: cleanNumberForInput(
      variant?.sellingPrice ?? variant?.selling_price
    ),
    isDefault: Boolean(
      variant?.isDefault ?? variant?.is_default ?? index === 0,
    ),
    isActive: variant?.isActive ?? variant?.is_active ?? true,
    sortOrder: variant?.sortOrder ?? variant?.sort_order ?? index,
    existingImages: images,
    newImages: [],
    coverSelection: cover
      ? { type: "existing", value: cover }
      : { type: "none", value: null },
  };
}

async function fetchAdmin(path, opts = {}) {
  const base =
    env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  const token = getAuthToken?.() || "";
  const res = await fetch(`${base}${path}`, {
    method: opts.method || "GET",
    headers: {
      accept: "application/json",
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      Authorization: token ? `Bearer ${token}` : "",
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { message: text || "Invalid JSON" };
  }
  if (!res.ok)
    throw new Error(json?.message || `Request failed (${res.status})`);
  return json;
}

function ImagePicker({
  title,
  existingImages = [],
  newImages = [],
  coverSelection,
  onAddFiles,
  onRemoveExisting,
  onRemoveNew,
  onSelectCover,
  fileInputRef,
  required = false,
  maxImages = 4,
}) {
  const allCount = existingImages.length + newImages.length;
  const canAddMore = allCount < maxImages;
  return (
    <div className="mt-4 rounded-[16px] border border-[#f2c6cc] bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Label>
            {title}
            {required ? " *" : ""}
          </Label>
          <div className="text-[12px] text-slate-500">
            Upload up to {maxImages} images and choose one cover image.
          </div>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;

              const remainingSlots = Math.max(maxImages - allCount, 0);
              const allowedFiles = files.slice(0, remainingSlots);

              if (allowedFiles.length) {
                onAddFiles(allowedFiles);
              }

              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
          <button
            type="button"
            disabled={!canAddMore}
            onClick={() => {
              if (!canAddMore) return;
              fileInputRef.current?.click();
            }}
            className={
              "inline-flex h-[42px] items-center gap-2 rounded-[10px] border px-4 text-[12px] font-semibold " +
              (canAddMore
                ? "border-[#f2c6cc] bg-white text-slate-600 hover:bg-slate-50"
                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400")
            }
          >
            <UploadCloud size={17} />
            {canAddMore
              ? allCount
                ? `Add More Images (${allCount}/${maxImages})`
                : `Choose Images (0/${maxImages})`
              : `Max ${maxImages} Images`}
          </button>
        </div>
      </div>

      {allCount ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {existingImages.map((img) => {
            const isCover =
              coverSelection?.type === "existing" &&
              coverSelection?.value === img.id;
            return (
              <div
                key={img.id}
                className="overflow-hidden rounded-[14px] border border-slate-200 bg-[#fcfcfc] p-2"
              >
                <div className="aspect-square overflow-hidden rounded-[10px] bg-slate-100">
                  <img
                    src={img.url}
                    alt="existing"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      onSelectCover({ type: "existing", value: img.id })
                    }
                    className={
                      "h-[34px] w-full rounded-[10px] border text-[11px] font-semibold " +
                      (isCover
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-[#f2c6cc] bg-white text-slate-600")
                    }
                  >
                    {isCover ? "Cover Image" : "Make Cover"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveExisting(img)}
                    className="h-[34px] w-full rounded-[10px] border border-rose-200 bg-rose-50 text-[11px] font-semibold text-rose-700"
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}

          {newImages.map((img, idx) => {
            const isCover =
              coverSelection?.type === "new" && coverSelection?.value === idx;
            return (
              <div
                key={`${img.name}-${idx}`}
                className="overflow-hidden rounded-[14px] border border-slate-200 bg-[#fcfcfc] p-2"
              >
                <div className="aspect-square overflow-hidden rounded-[10px] bg-slate-100">
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mt-2 truncate text-[11px] text-slate-500">
                  {img.name}
                </div>
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => onSelectCover({ type: "new", value: idx })}
                    className={
                      "h-[34px] w-full rounded-[10px] border text-[11px] font-semibold " +
                      (isCover
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-[#f2c6cc] bg-white text-slate-600")
                    }
                  >
                    {isCover ? "Cover Image" : "Make Cover"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveNew(idx)}
                    className="h-[34px] w-full rounded-[10px] border border-rose-200 bg-rose-50 text-[11px] font-semibold text-rose-700"
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 rounded-[12px] border border-dashed border-slate-300 px-4 py-6 text-center text-[12px] text-slate-400">
          No images selected.
        </div>
      )}
    </div>
  );
}

export default function AddProduct() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [openCatalog, setOpenCatalog] = useState(false);
  const [catalogTab, setCatalogTab] = useState("categories");
  const [saving, setSaving] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [lookups, setLookups] = useState({
    loading: false,
    error: "",
    categories: [],
    brands: [],
    units: [],
  });
  const [slugTouched, setSlugTouched] = useState(false);

  const [form, setForm] = useState({
    sku: "",
    barcode: "",
    name: "",
    slug: "",
    mainCategoryId: "",
    categoryGroupId: "",
    categoryId: "",
    categoryName: "",
    subCategoryName: "",
    brandName: "",
    unitSymbol: "",
    shortDescription: "",
    description: "",
    status: "active",
    isActive: true,
    taxPercent: "",
    weightValue: "",
    weightUnitSymbol: "",
  });
  const [variants, setVariants] = useState([createEmptyVariant(0)]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [coverSelection, setCoverSelection] = useState({
    type: "none",
    value: null,
  });
  const productFileRef = useRef(null);
  const variantFileRefs = useRef({});
  const getVariantFileRef = (index) => {
    if (!variantFileRefs.current[index])
      variantFileRefs.current[index] = React.createRef();
    return variantFileRefs.current[index];
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // useEffect(() => {
  //   if (!msg?.text) return;
  //   const t = setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  //   return () => clearTimeout(t);
  // }, [msg?.text]);

  const loadLookups = useCallback(async () => {
    try {
      setLookups((s) => ({ ...s, loading: true, error: "" }));

      const [categories, brands, units] = await Promise.all([
        fetchAdmin("/api/admin/categories?limit=500"),
        fetchAdmin("/api/admin/brands?limit=500"),
        fetchAdmin("/api/admin/units?limit=500"),
      ]);

      setLookups({
        loading: false,
        error: "",
        categories: normalizeList(categories).map(normalizeCategory),
        brands: normalizeList(brands),
        units: normalizeList(units),
      });
    } catch (e) {
      setLookups((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Failed to load lookups",
      }));
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!isEdit) return;
    if (lookups.loading) return;
    if (!Array.isArray(lookups.categories) || lookups.categories.length === 0) return;
    let alive = true;
    (async () => {
      try {
        setLoadingProduct(true);
        const res = await productsService.getProductById(id);
        const item = res?.item || res?.product || res?.data?.item || null;
        if (!alive || !item) return;

        const allCategories = Array.isArray(lookups.categories)
          ? lookups.categories.map(normalizeCategory)
          : [];

        const directCategory =
          allCategories.find(
            (cat) => String(cat?.id) === String(item?.categoryId || ""),
          ) ||
          allCategories.find(
            (cat) => safeLower(cat?.name) === safeLower(item?.categoryName),
          ) ||
          null;

        const selectedCategoryPath = directCategory
          ? buildCategoryPathOptions(allCategories).find(
            (option) => String(option.value) === String(directCategory.id),
          )?.path || [directCategory]
          : [];

        const mainCategory = selectedCategoryPath[0] || null;
        const categoryGroup = selectedCategoryPath[1] || null;

        setForm({
          sku: item?.sku || "",
          barcode: item?.barcode || "",
          name: item?.name || "",
          slug: item?.slug || "",
          mainCategoryId: mainCategory?.id || "",
          categoryGroupId: categoryGroup?.id || "",
          categoryId: item?.categoryId || directCategory?.id || "",
          categoryName: mainCategory?.name || "",
          subCategoryName: categoryGroup?.name || "",
          brandName: item?.brandName || "",
          unitSymbol: item?.unitSymbol || item?.unitName || "",
          shortDescription: item?.shortDescription || "",
          description: item?.description || "",
          status: item?.status || "active",
          isActive: item?.isActive ?? true,
          taxPercent: cleanNumberForInput(item?.taxPercent),
          weightValue: cleanNumberForInput(item?.weightValue),
          weightUnitSymbol: item?.weightUnitSymbol ?? "",
        });
        const images = Array.isArray(item?.images)
          ? item.images.map((img, idx) => ({
            id: img?.id || `${idx}`,
            url: resolveImageUrl(
              img?.url || img?.imageUrl || img?.image_url || "",
            ),
            rawUrl: img?.url || img?.imageUrl || img?.image_url || "",
            isPrimary: Boolean(
              img?.isPrimary ?? img?.is_primary ?? idx === 0,
            ),
          }))
          : [];
        const productCover =
          images.find((img) => img.isPrimary)?.id || images[0]?.id || null;
        setExistingImages(images);
        setCoverSelection(
          productCover
            ? { type: "existing", value: productCover }
            : { type: "none", value: null },
        );
        setVariants(
          (Array.isArray(item?.variants) && item.variants.length
            ? item.variants
            : [createEmptyVariant(0)]
          ).map(normalizeIncomingVariant),
        );
      } catch (e) {
        if (alive)
          setMsg({ type: "err", text: e?.message || "Failed to load product" });
      } finally {
        if (alive) setLoadingProduct(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, isEdit, lookups.loading, lookups.categories]);

  useEffect(() => {
    if (slugTouched) return;
    setForm((s) => ({ ...s, slug: toSlug(s.name || s.sku || "") }));
  }, [form.name, form.sku, slugTouched]);

  const categoryBuckets = useMemo(() => {
    const all = Array.isArray(lookups.categories)
      ? lookups.categories.map(normalizeCategory)
      : [];

    const parents = all
      .filter((cat) => !cat?.parent_id)
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );

    const children = all
      .filter((cat) => !!cat?.parent_id)
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );

    const childrenByParent = children.reduce((acc, item) => {
      const key = String(item?.parent_id || "");
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    return { parents, children, childrenByParent };
  }, [lookups.categories]);

  const selectedMainCategory = useMemo(() => {
    return (
      categoryBuckets.parents.find(
        (cat) => String(cat?.id || "") === String(form.mainCategoryId || ""),
      ) ||
      categoryBuckets.parents.find(
        (cat) =>
          safeLower(cat?.name) === safeLower(form.categoryName) ||
          safeLower(cat?.slug) === safeLower(form.categoryName),
      ) ||
      null
    );
  }, [categoryBuckets.parents, form.mainCategoryId, form.categoryName]);

  const middleCategoryOptions = useMemo(() => {
    if (!selectedMainCategory?.id) return [];
    return categoryBuckets.childrenByParent[String(selectedMainCategory.id)] || [];
  }, [categoryBuckets.childrenByParent, selectedMainCategory]);

  const selectedMiddleCategory = useMemo(() => {
    return (
      middleCategoryOptions.find(
        (cat) => String(cat?.id || "") === String(form.categoryGroupId || ""),
      ) ||
      middleCategoryOptions.find(
        (cat) =>
          safeLower(cat?.name) === safeLower(form.subCategoryName) ||
          safeLower(cat?.slug) === safeLower(form.subCategoryName),
      ) ||
      null
    );
  }, [middleCategoryOptions, form.categoryGroupId, form.subCategoryName]);

  const leafSubcategoryOptions = useMemo(() => {
    if (!selectedMiddleCategory?.id) return [];
    const directChildren =
      categoryBuckets.childrenByParent[String(selectedMiddleCategory.id)] || [];
    return directChildren.filter(
      (cat) =>
        (categoryBuckets.childrenByParent[String(cat?.id || "")] || []).length === 0,
    );
  }, [categoryBuckets.childrenByParent, selectedMiddleCategory]);

  const selectedCategoryPath = useMemo(() => {
    const selectedId = String(form.categoryId || "");
    if (!selectedId) return null;
    return buildCategoryPathOptions(lookups.categories || []).find(
      (option) => option.value === selectedId,
    ) || null;
  }, [lookups.categories, form.categoryId]);

  const statusOptions = useMemo(
    () => [
      { value: "active", label: "active" },
      { value: "inactive", label: "inactive" },
    ],
    [],
  );

  const fetchFromBarcode = async () => {
    try {
      setMsg({ type: "", text: "" });
      const code = String(form.barcode || "").trim();
      if (!code)
        return setMsg({ type: "err", text: "Please enter barcode first" });
      const res = await productsService.previewByBarcode(code);
      const item =
        res?.suggested || res?.item || res?.data?.item || res?.product || null;
      const mapped = res?.mapped || {};
      if (!item)
        return setMsg({
          type: "err",
          text: "Barcode preview succeeded, but product data missing",
        });
      const qty = mapped?.quantity;
      const { value: qtyValue } = parseQuantity(qty);
      setSlugTouched(Boolean(item.slug));
      setForm((s) => ({
        ...s,
        sku: item.sku || s.sku || code,
        barcode: item.barcode || s.barcode || code,
        name: item.name || s.name,
        slug: item.slug || s.slug,
        status: item.status || s.status,
        isActive: item.isActive ?? s.isActive,
        brandName: mapped?.brandName || s.brandName,
        categoryName: mapped?.categoryName || s.categoryName,
        unitSymbol: mapped?.unitSymbol || s.unitSymbol,
        weightValue: qtyValue || s.weightValue,
        shortDescription: s.shortDescription || item.name || s.name,
      }));
      setMsg({ type: "ok", text: "Fetched from barcode" });
    } catch (e) {
      setMsg({ type: "err", text: e?.message || "Failed to fetch by barcode" });
    }
  };

  const resolveIdsFromText = () => {
    const brandName = safeLower(form.brandName);
    const categoryName = safeLower(form.categoryName);
    const subCategoryName = safeLower(form.subCategoryName);
    const unitSymbol = safeLower(form.unitSymbol);

    const brandId =
      (lookups.brands || []).find(
        (b) =>
          safeLower(b?.name) === brandName || safeLower(b?.slug) === brandName,
      )?.id || null;

    const parentCategory =
      (categoryBuckets.parents || []).find(
        (c) =>
          String(c?.id || "") === String(form.mainCategoryId || "") ||
          safeLower(c?.name) === categoryName ||
          safeLower(c?.slug) === categoryName,
      ) || null;

    const subCategory =
      (
        categoryBuckets.childrenByParent[String(parentCategory?.id || "")] || []
      ).find(
        (c) =>
          String(c?.id || "") === String(form.categoryGroupId || "") ||
          safeLower(c?.name) === subCategoryName ||
          safeLower(c?.slug) === subCategoryName,
      ) || null;

    const unitId =
      (lookups.units || []).find(
        (u) =>
          safeLower(u?.symbol) === unitSymbol ||
          safeLower(u?.name) === unitSymbol,
      )?.id || null;

    const selectedCategoryId = String(form.categoryId || "").trim() || subCategory?.id || null;

    return {
      brandId,
      parentCategoryId: parentCategory?.id || null,
      subCategoryId: subCategory?.id || null,
      categoryId: selectedCategoryId,
      unitId,
    };
  };

  async function ensureBrandId(name, currentLookups) {
    const brandName = safeLower(name);
    if (!brandName) return null;
    const existing = (currentLookups.brands || []).find(
      (b) => safeLower(b.name) === brandName || safeLower(b.slug) === brandName,
    );
    if (existing) return existing.id;
    const json = await fetchAdmin("/api/admin/brands/", {
      method: "POST",
      body: { name, slug: toSlug(name) },
    });
    return json?.item?.id || json?.id || null;
  }

  async function ensureParentCategoryId(name, currentLookups) {
    const categoryName = safeLower(name);
    if (!categoryName) return null;

    const existing = (currentLookups.categories || [])
      .map(normalizeCategory)
      .find(
        (c) =>
          !c?.parent_id &&
          (safeLower(c?.name) === categoryName ||
            safeLower(c?.slug) === categoryName),
      );

    if (existing) return existing.id;

    const json = await fetchAdmin("/api/admin/categories/", {
      method: "POST",
      body: { name, slug: toSlug(name) },
    });

    return json?.item?.id || json?.id || null;
  }

  async function ensureSubCategoryId(name, parentId, currentLookups) {
    const categoryName = safeLower(name);
    if (!categoryName || !parentId) return null;

    const existing = (currentLookups.categories || [])
      .map(normalizeCategory)
      .find(
        (c) =>
          String(c?.parent_id || "") === String(parentId) &&
          (safeLower(c?.name) === categoryName ||
            safeLower(c?.slug) === categoryName),
      );

    if (existing) return existing.id;

    const json = await fetchAdmin("/api/admin/categories/", {
      method: "POST",
      body: { name, slug: toSlug(name), parentId },
    });

    return json?.item?.id || json?.id || null;
  }

  async function ensureUnitId(symbol, currentLookups) {
    const unitSymbol = safeLower(symbol);
    if (!unitSymbol) return null;
    const existing = (currentLookups.units || []).find(
      (u) =>
        safeLower(u.symbol) === unitSymbol || safeLower(u.name) === unitSymbol,
    );
    if (existing) return existing.id;
    const json = await fetchAdmin("/api/admin/units/", {
      method: "POST",
      body: { symbol, name: symbol },
    });
    return json?.item?.id || json?.id || null;
  }

  const updateVariant = (index, patch) => {
    setVariants((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)),
    );
  };

  const addVariantFiles = (index, files) => {
    const mapped = files.map(createImagePreview);
    setVariants((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const nextImages = [...item.newImages, ...mapped];
        const nextCover =
          item.coverSelection?.value != null
            ? item.coverSelection
            : { type: "new", value: 0 };
        return { ...item, newImages: nextImages, coverSelection: nextCover };
      }),
    );
  };

  const removeVariantNewImage = (index, imageIndex) => {
    setVariants((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const nextImages = item.newImages.filter((_, i) => i !== imageIndex);
        let nextCover = item.coverSelection;
        if (item.coverSelection?.type === "new") {
          if (item.coverSelection.value === imageIndex) {
            nextCover = item.existingImages[0]?.id
              ? { type: "existing", value: item.existingImages[0].id }
              : nextImages[0]
                ? { type: "new", value: 0 }
                : { type: "none", value: null };
          } else if (item.coverSelection.value > imageIndex) {
            nextCover = {
              ...item.coverSelection,
              value: item.coverSelection.value - 1,
            };
          }
        }
        return { ...item, newImages: nextImages, coverSelection: nextCover };
      }),
    );
  };

  const removeVariantExistingImage = async (index, image) => {
    const variant = variants[index];
    if (!isEdit || !variant?.id || !image?.id) {
      setVariants((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
              ...item,
              existingImages: item.existingImages.filter(
                (img) => img.id !== image.id,
              ),
            }
            : item,
        ),
      );
      return;
    }
    try {
      await productsService.deleteVariantImage(id, variant.id, image.id);
      setVariants((prev) =>
        prev.map((item, idx) => {
          if (idx !== index) return item;
          const nextExisting = item.existingImages.filter(
            (img) => img.id !== image.id,
          );
          let nextCover = item.coverSelection;
          if (nextCover?.type === "existing" && nextCover?.value === image.id) {
            nextCover = nextExisting[0]?.id
              ? { type: "existing", value: nextExisting[0].id }
              : item.newImages[0]
                ? { type: "new", value: 0 }
                : { type: "none", value: null };
          }
          return {
            ...item,
            existingImages: nextExisting,
            coverSelection: nextCover,
          };
        }),
      );
      setMsg({ type: "ok", text: "Variant image removed" });
    } catch (e) {
      setMsg({
        type: "err",
        text: e?.message || "Failed to remove variant image",
      });
    }
  };

  const removeExistingProductImage = async (image) => {
    if (!isEdit || !image?.id) {
      setExistingImages((prev) => prev.filter((img) => img.id !== image.id));
      return;
    }
    try {
      await productsService.deleteProductImage(id, image.id);
      setExistingImages((prev) => prev.filter((img) => img.id !== image.id));
      setCoverSelection((current) =>
        current?.type === "existing" && current?.value === image.id
          ? { type: "none", value: null }
          : current,
      );
      setMsg({ type: "ok", text: "Product image removed" });
    } catch (e) {
      setMsg({
        type: "err",
        text: e?.message || "Failed to remove product image",
      });
    }
  };

  const removeNewProductImage = (imageIndex) => {
    setNewImages((prev) => prev.filter((_, i) => i !== imageIndex));
    setCoverSelection((current) => {
      if (current?.type !== "new") return current;
      if (current.value === imageIndex)
        return existingImages[0]?.id
          ? { type: "existing", value: existingImages[0].id }
          : { type: "none", value: null };
      if (current.value > imageIndex)
        return { ...current, value: current.value - 1 };
      return current;
    });
  };

  const addVariant = () =>
    setVariants((prev) => [...prev, createEmptyVariant(prev.length)]);
  const removeVariant = (index) => {
    setVariants((prev) => {
      if (prev.length === 1) return [createEmptyVariant(0)];
      const next = prev
        .filter((_, idx) => idx !== index)
        .map((item, idx) => ({ ...item, isDefault: false, sortOrder: idx }));
      if (!next.some((item) => item.isDefault)) next[0].isDefault = true;
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const sku = String(form.sku || "").trim();
    const name = String(form.name || "").trim();
    const slug = String(form.slug || "").trim();
    const barcode = String(form.barcode || "").trim();

    const isNumber = (value) => {
      const text = String(value ?? "").trim();
      return text !== "" && /^\d+(\.\d+)?$/.test(text);
    };

    const isText = (value) => {
      const text = String(value ?? "").trim();
      return text !== "" && /[a-zA-Z]/.test(text);
    };

    const isAlphaNumeric = (value) => {
      const text = String(value ?? "").trim();
      return text !== "" && /^[a-zA-Z0-9_-]+$/.test(text);
    };

    const isSafeAmount = (value) => {
      const text = String(value ?? "").trim();
      return text !== "" && /^\d{1,8}(\.\d{1,2})?$/.test(text);
    };

    const isSafeOptionalAmount = (value) => {
      const text = String(value ?? "").trim();
      return text === "" || /^\d{1,8}(\.\d{1,2})?$/.test(text);
    };

    const toNullableNumber = (value) => {
      const text = String(value ?? "").trim();
      return text === "" ? null : Number(text);
    };
    if (!sku) return setMsg({ type: "err", text: "SKU is required" });
    if (!name) return setMsg({ type: "err", text: "Product name is required" });
    if (!slug) return setMsg({ type: "err", text: "Slug is required" });
    if (!String(form.categoryId || "").trim())
      return setMsg({ type: "err", text: "Product category is required" });
    if (!isAlphaNumeric(sku)) {
      return setMsg({
        type: "err",
        text: "SKU can contain only letters, numbers, hyphen and underscore.",
      });
    }

    if (barcode && !/^\d{1,15}$/.test(barcode)){
      return setMsg({
        type: "err",
        text: "Barcode must contain numbers only.",
      });
    }

    if (!isText(name)) {
      return setMsg({
        type: "err",
        text: "Product name must contain text.",
      });
    }

    if (form.taxPercent !== "" && !isNumber(form.taxPercent)) {
      return setMsg({
        type: "err",
        text: "Tax percent must be a number.",
      });
    }

    if (!isSafeOptionalAmount(form.weightValue)) {
      return setMsg({
        type: "err",
        text: "Product weight must be a valid number. Maximum 8 digits and 2 decimals allowed.",
      });
    }
    const parentImageCount = existingImages.length + newImages.length;

    if (parentImageCount === 0) {
      return setMsg({
        type: "err",
        text: "Please upload at least 1 product image.",
      });
    }

    try {
      setSaving(true);
      let { unitId, categoryId } = resolveIdsFromText();
      const brandId = await ensureBrandId(form.brandName, lookups);
      categoryId = String(form.categoryId || categoryId || "").trim() || null;
      if (!unitId && form.unitSymbol)
        unitId = await ensureUnitId(form.unitSymbol, lookups);
      if (!categoryId) throw new Error("Please select product category");

      const cleanVariants = variants
        .map((variant, index) => ({
          _sourceIndex: index,
          id: variant.id || undefined,
          variantName: String(variant.variantName || "").trim(),
          variantSku: String(variant.variantSku || "").trim() || null,
          barcode: String(variant.barcode || "").trim() || null,
          packLabel: String(variant.packLabel || "").trim() || null,
          unitValue: toNullableNumber(variant.unitValue),
          unitSymbol: variant.unitSymbol || null,
          weightValue: toNullableNumber(variant.weightValue),
          weightUnitSymbol: variant.weightUnitSymbol || null,
          variantGroup: String(variant.variantGroup || "").trim() || null,
          variantValue: String(variant.variantValue || "").trim() || null,
          shortDescription:
            String(variant.shortDescription || "").trim() || null,
          description: String(variant.description || "").trim() || null,
          mrp: toNullableNumber(variant.mrp),
          sellingPrice: toNullableNumber(variant.sellingPrice),
          isDefault: Boolean(variant.isDefault),
          isActive: variant.isActive !== false,
          sortOrder: index,
        }))
        .filter(
          (variant) =>
            variant.variantName ||
            variant.variantSku ||
            variant.barcode ||
            variant.packLabel ||
            variant.unitValue ||
            variant.weightValue ||
            variant.mrp ||
            variant.sellingPrice ||
            variant.variantGroup ||
            variant.variantValue ||
            variant.shortDescription ||
            variant.description,
        );

      if (!cleanVariants.length)
        throw new Error("At least one actual variant is required");
      if (!cleanVariants.some((variant) => variant.isDefault))
        cleanVariants[0].isDefault = true;

      const variantKeys = new Set();

      for (let i = 0; i < cleanVariants.length; i += 1) {
        const v = cleanVariants[i];

        const originalVariant = variants[v._sourceIndex];

        if (!isText(v.variantName)) {
          throw new Error(`Variant ${i + 1}: Variant name must contain text.`);
        }

        if (!isAlphaNumeric(v.variantSku)) {
          throw new Error(
            `Variant ${i + 1}: Variant SKU can contain only letters, numbers, hyphen and underscore.`
          );
        }

        if (v.barcode && !/^\d{1,15}$/.test(v.barcode)) {
          throw new Error(`Variant ${i + 1}: Barcode must contain numbers only.`);
        }
        if (v.unitValue != null && !isSafeAmount(v.unitValue)) {
          throw new Error(
            `Variant ${i + 1}: Qty / Pack value must be a valid number. Maximum 8 digits and 2 decimals allowed.`
          );
        }

        if (v.weightValue != null && !isSafeAmount(v.weightValue)) {
          throw new Error(
            `Variant ${i + 1}: Weight value must be a valid number. Maximum 8 digits and 2 decimals allowed.`
          );
        }

        if (v.mrp != null && !isSafeAmount(v.mrp)) {
          throw new Error(
            `Variant ${i + 1}: MRP must be a valid number. Maximum 8 digits and 2 decimals allowed.`
          );
        }

        if (v.sellingPrice != null && !isSafeAmount(v.sellingPrice)) {
          throw new Error(
            `Variant ${i + 1}: Selling price must be a valid number. Maximum 8 digits and 2 decimals allowed.`
          );
        }

        if (!v.variantName) {
          throw new Error(`Variant ${i + 1}: Variant name is required`);
        }

        if (!v.variantSku) {
          throw new Error(`Variant ${i + 1}: Variant SKU is required`);
        }

        if (!v.variantGroup) {
          throw new Error(`Variant ${i + 1}: Variant group is required`);
        }

        if (!v.variantValue) {
          throw new Error(`Variant ${i + 1}: Variant value is required`);
        }

        if (!v.packLabel) {
          throw new Error(`Variant ${i + 1}: Pack label is required`);
        }

        if (v.mrp == null || Number(v.mrp) <= 0) {
          throw new Error(`Variant ${i + 1}: MRP is required`);
        }

        if (v.sellingPrice == null || Number(v.sellingPrice) <= 0) {
          throw new Error(`Variant ${i + 1}: Selling price is required`);
        }


        const variantImageCount =
          (originalVariant?.existingImages?.length || 0) +
          (originalVariant?.newImages?.length || 0);

        if (variantImageCount === 0) {
          throw new Error(
            `Variant ${i + 1} (${v.variantName}): Please upload at least 1 image.`
          );
        }

        if (Number(v.sellingPrice) > Number(v.mrp)) {
          throw new Error(`Variant ${i + 1}: Selling price cannot be greater than MRP`);
        }

        const key = [
          String(v.variantName || "").trim().toLowerCase(),
          String(v.variantSku || "").trim().toLowerCase(),
        ].join("::");

        if (variantKeys.has(key)) {
          throw new Error(
            `Variant ${i + 1}: Duplicate variant name/SKU found. Please make variant name and SKU different.`
          );
        }

        variantKeys.add(key);
      }

      const payload = {
        sku,
        barcode: barcode || null,
        name,
        slug,
        imageUrl: null,
        categoryId,
        brandId,
        unitId,
        shortDescription: form.shortDescription || null,
        description: form.description || null,
        status: form.status || "active",
        isActive: String(form.status || "active").toLowerCase() === "active",
        taxPercent: toNullableNumber(form.taxPercent),
        weightValue: toNullableNumber(form.weightValue),
        weightUnitSymbol: form.weightUnitSymbol || null,
        variants: cleanVariants.map(({ _sourceIndex, ...variant }) => variant),
      };

      let productRes;
      if (isEdit) productRes = await productsService.updateProduct(id, payload);
      else productRes = await productsService.upsertProduct(payload);

      const savedProduct =
        productRes?.item ||
        productRes?.product ||
        productRes?.data?.item ||
        productRes?.data?.product ||
        null;
      const productId =
        savedProduct?.id || id || productRes?.id || productRes?.productId;
      if (!productId)
        throw new Error("Product saved, but product id not returned by API");

      if (newImages.length > 0) {
        const primaryIndex =
          coverSelection?.type === "new" &&
            Number.isInteger(coverSelection?.value)
            ? coverSelection.value
            : null;
        await productsService.uploadProductImages(
          productId,
          newImages.map((img) => img.file),
          primaryIndex,
        );
      }
      if (coverSelection?.type === "existing" && coverSelection?.value) {
        await productsService.setPrimaryProductImage(
          productId,
          coverSelection.value,
        );
      }

      const finalRes = await productsService.getProductById(productId);
      const finalProduct = finalRes?.item || finalRes?.product || null;
      const savedVariants = Array.isArray(finalProduct?.variants)
        ? finalProduct.variants
        : [];

      for (let index = 0; index < variants.length; index += 1) {
        const localVariant = variants[index];
        const localNewImages = Array.isArray(localVariant?.newImages)
          ? localVariant.newImages
          : [];
        const hasExistingPrimary =
          localVariant?.coverSelection?.type === "existing" &&
          localVariant?.coverSelection?.value;
        const hasNewPrimary =
          localVariant?.coverSelection?.type === "new" &&
          Number.isInteger(localVariant?.coverSelection?.value);
        const serverVariant = savedVariants.find((item) => {
          if (
            localVariant?.id &&
            item?.id &&
            String(item.id) === String(localVariant.id)
          )
            return true;
          if (
            localVariant?.variantSku &&
            item?.variantSku &&
            String(item.variantSku).trim() ===
            String(localVariant.variantSku).trim()
          )
            return true;
          return (
            String(item?.variantName || "").trim() ===
            String(localVariant?.variantName || "").trim() &&
            String(item?.packLabel || "").trim() ===
            String(localVariant?.packLabel || "").trim()
          );
        });
        if (!serverVariant?.id) continue;
        if (localNewImages.length > 0) {
          await productsService.uploadVariantImages(
            productId,
            serverVariant.id,
            localNewImages.map((img) => img.file),
            hasNewPrimary ? localVariant.coverSelection.value : null,
          );
        }
        if (hasExistingPrimary) {
          await productsService.setPrimaryVariantImage(
            productId,
            serverVariant.id,
            localVariant.coverSelection.value,
          );
        }
      }

      setMsg({
        type: "ok",
        text: isEdit ? "Product updated" : "Product saved",
      });
      setTimeout(() => nav(`/admin/dashboard/products/${productId}`), 400);
    } catch (err) {
      setMsg({
        type: "err",
        text: err?.message || `Failed to ${isEdit ? "update" : "save"} product`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full relative">
      {msg.text ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/35 px-4">
          <div className="w-full max-w-[420px] rounded-[18px] bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div
                  className={
                    "mb-2 text-[16px] font-bold " +
                    (msg.type === "ok" ? "text-emerald-600" : "text-rose-600")
                  }
                >
                  {msg.type === "ok" ? "Success" : "Please Check Details"}
                </div>

                <div className="text-[13px] font-medium leading-6 text-slate-600">
                  {msg.text}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMsg({ type: "", text: "" })}
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setMsg({ type: "", text: "" })}
                className={
                  "h-[38px] rounded-[10px] px-5 text-[13px] font-semibold text-white " +
                  (msg.type === "ok"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-[#d86a78] hover:bg-[#c95b69]")
                }
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Go back"
          title="Back"
          onClick={() => nav("/admin/dashboard/products")}
          className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-[#d86a78] shadow-sm transition hover:bg-[#fff1f3] hover:text-[#c95b69]"
        >
          <ArrowLeft size={18} strokeWidth={2.4} />
        </button>

        <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
          {isEdit ? "EDIT PRODUCT" : "ADD PRODUCT"}
        </h1>
      </div>

      {lookups.error ? (
        <div className="mt-3 text-[12px] text-rose-600">{lookups.error}</div>
      ) : null}
      {loadingProduct ? (
        <div className="mt-3 text-[12px] text-slate-500">
          Loading product...
        </div>
      ) : null}

      <form
        onSubmit={onSubmit}
        className="mt-6 rounded-[14px] border border-slate-200/70 bg-white p-5"
      >
        <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
          <div className="mb-4">
            <div className="text-[18px] font-bold text-[#d86a78]">
              Product Information
            </div>
            <div className="text-[12px] text-slate-500">
              Add the common master product here. Actual sellable variants will be added below.
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <Label>SKU *</Label>
              <Input
                value={form.sku}
                onChange={(e) => set("sku", e.target.value)}
                placeholder="WS-SKU-0001"
              />
            </div>
            <div>
              <Label>Barcode</Label>
              <div className="flex gap-2">
                <Input
                  value={form.barcode}
                  maxLength={15}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,15}$/.test(value)) {
                      set("barcode", value);
                    }
                  }}
                  placeholder="8901063010100"
                />
                <button
                  type="button"
                  onClick={fetchFromBarcode}
                  className="h-[44px] whitespace-nowrap rounded-[10px] bg-[#d86a78] px-4 text-[12px] font-semibold text-white hover:bg-[#c95b69]"
                >
                  Fetch
                </button>
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <SelectLike
                value={form.status}
                onChange={(v) => set("status", v)}
                placeholder="Select"
                options={statusOptions}
              />
            </div>

            <div className="lg:col-span-2">
              <Label>Product Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Lays Chips / Rose / Cold Drink"
              />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug", e.target.value);
                }}
                placeholder="auto-generated"
              />
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-rose-100 bg-white/80 p-4 shadow-[0_4px_16px_rgba(15,23,42,0.03)]">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Label>Product Category *</Label>
                  <div className="mt-1 text-[11px] font-medium text-slate-400">
                    Select in order: Main Category → Category → Subcategory
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCatalogTab("categories");
                    setOpenCatalog(true);
                  }}
                  className="h-[38px] shrink-0 rounded-[10px] border border-[#e8b5bd] bg-[#fff7f8] px-3 text-[12px] font-bold text-[#b85362] hover:bg-[#fff0f2]"
                  title="Manage categories"
                >
                  Manage Categories
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><span className="grid h-5 w-5 place-items-center rounded-md bg-rose-50 text-[10px] font-black text-[#b85362]">1</span>Main Category</div>
                  <SelectLike
                    value={form.mainCategoryId}
                    onChange={(v) => {
                      const selected = categoryBuckets.parents.find((cat) => String(cat.id) === String(v));
                      setForm((s) => ({
                        ...s,
                        mainCategoryId: v,
                        categoryGroupId: "",
                        categoryId: "",
                        categoryName: selected?.name || "",
                        subCategoryName: "",
                      }));
                    }}
                    placeholder={lookups.loading ? "Loading..." : "Select Main Category"}
                    options={categoryBuckets.parents.map((cat) => ({ value: String(cat.id), label: cat.name }))}
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><span className="grid h-5 w-5 place-items-center rounded-md bg-rose-50 text-[10px] font-black text-[#b85362]">2</span>Category</div>
                  <SelectLike
                    value={form.categoryGroupId}
                    onChange={(v) => {
                      const selected = middleCategoryOptions.find((cat) => String(cat.id) === String(v));
                      setForm((s) => ({
                        ...s,
                        categoryGroupId: v,
                        categoryId: "",
                        subCategoryName: selected?.name || "",
                      }));
                    }}
                    placeholder={selectedMainCategory ? "Select Category" : "Select Main Category first"}
                    options={middleCategoryOptions.map((cat) => ({ value: String(cat.id), label: cat.name }))}
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><span className="grid h-5 w-5 place-items-center rounded-md bg-emerald-50 text-[10px] font-black text-emerald-700">3</span>Subcategory</div>
                  <SelectLike
                    value={form.categoryId}
                    onChange={(v) => set("categoryId", v)}
                    placeholder={selectedMiddleCategory ? "Select Subcategory" : "Select Category first"}
                    options={leafSubcategoryOptions.map((cat) => ({ value: String(cat.id), label: cat.name }))}
                  />
                </div>
              </div>

              {selectedCategoryPath ? (
                <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px] font-medium text-slate-500">
                  Selected: {selectedCategoryPath.label}
                  {selectedCategoryPath.path?.length !== 3 ? (
                    <span className="ml-2 font-semibold text-amber-600">Legacy category — keep it only for existing product edits or move the product to a level-3 Subcategory.</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div>
              <Label>Brand</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SelectLike
                    value={form.brandName}
                    onChange={(v) => set("brandName", v)}
                    placeholder={
                      lookups.loading ? "Loading..." : "Select brand"
                    }
                    options={(lookups.brands || []).map((b) => ({
                      value: b.name || "",
                      label: b.name || "",
                    }))}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCatalogTab("brands");
                    setOpenCatalog(true);
                  }}
                  className="h-[44px] min-w-[44px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[18px] font-semibold text-[#d86a78] hover:bg-slate-50"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <Label>Unit</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SelectLike
                    value={form.unitSymbol}
                    onChange={(v) => set("unitSymbol", v)}
                    placeholder={lookups.loading ? "Loading..." : "Select unit"}
                    options={(lookups.units || []).map((u) => ({
                      value: u.symbol || u.name || "",
                      label:
                        u.symbol &&
                          u.name &&
                          String(u.symbol).trim().toLowerCase() !==
                          String(u.name).trim().toLowerCase()
                          ? `${u.symbol} - ${u.name}`
                          : u.symbol || u.name || "",
                    }))}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCatalogTab("units");
                    setOpenCatalog(true);
                  }}
                  className="h-[44px] min-w-[44px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[18px] font-semibold text-[#d86a78] hover:bg-slate-50"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <Label>Tax %</Label>
              <QuickSelectInput
                value={form.taxPercent}
                onChange={(v) => set("taxPercent", v)}
                placeholder="Select tax %"
                listId="tax-percent-options"
                options={TAX_PERCENT_OPTIONS}
                inputMode="decimal"
              />
            </div>
            <div>
              <Label>Weight</Label>
              <Input
                value={form.weightValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) {
                    set("weightValue", value);
                  }
                }}
                placeholder="Optional"
                inputMode="decimal"
              />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <Label>Short Description</Label>
              <Input
                value={form.shortDescription}
                onChange={(e) => set("shortDescription", e.target.value)}
                placeholder="Common short description"
              />
            </div>
            {/* <div>
              <Label>Parent Active</Label>
              <Toggle
                value={form.isActive}
                onChange={(v) => set("isActive", v)}
              />
            </div> */}
          </div>

          <div className="mt-5">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Common description"
            />
          </div>

          <ImagePicker
            title="Product Images"
            required
            maxImages={4}
            existingImages={existingImages}
            newImages={newImages}
            coverSelection={coverSelection}
            fileInputRef={productFileRef}
            onAddFiles={(files) => {
              const mapped = files.map(createImagePreview);
              setNewImages((prev) => [...prev, ...mapped]);
              setCoverSelection((current) =>
                current?.value != null ? current : { type: "new", value: 0 },
              );
            }}
            onRemoveExisting={removeExistingProductImage}
            onRemoveNew={removeNewProductImage}
            onSelectCover={setCoverSelection}
          />
        </div>

        <div className="mt-6 rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Actual Product Variants</Label>
              <div className="text-[12px] text-slate-500">
                Each variant includes its own name, size/flavor/color, description, price, and images.
              </div>
            </div>
            <button
              type="button"
              onClick={addVariant}
              className="rounded-[10px] border border-[#f2c6cc] bg-white px-4 py-2 text-[12px] font-semibold text-[#d86a78]"
            >
              + Add Variant
            </button>
          </div>

          <div className="mt-4 space-y-4">
            {variants.map((variant, index) => (
              <div
                key={variant.id || index}
                className="rounded-[16px] border border-[#f2c6cc] bg-white p-4"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[14px] font-semibold text-slate-700">
                      Variant {index + 1}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Example: Green Lays, Red Rose, 500ml Bottle
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-slate-600">
                      <input
                        type="radio"
                        name="defaultVariant"
                        checked={!!variant.isDefault}
                        onChange={() =>
                          setVariants((prev) =>
                            prev.map((item, idx) => ({
                              ...item,
                              isDefault: idx === index,
                            })),
                          )
                        }
                      />
                      Default
                    </label>
                    <button
                      type="button"
                      onClick={() => removeVariant(index)}
                      className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <Label>Variant Name *</Label>
                    <Input
                      value={variant.variantName}
                      onChange={(e) =>
                        updateVariant(index, { variantName: e.target.value })
                      }
                      placeholder="Green Lays / Red Rose / 500ml"
                    />
                  </div>
                  <div>
                    <Label>Variant Group</Label>
                    <FreeTextSelectInput
                      value={variant.variantGroup}
                      onChange={(v) => updateVariant(index, { variantGroup: v })}
                      placeholder="Select or type variant group"
                      listId={`variant-group-options-${index}`}
                      options={VARIANT_GROUP_OPTIONS}
                    />
                  </div>
                  <div>
                    <Label>Variant Value</Label>
                    <Input
                      value={variant.variantValue}
                      onChange={(e) =>
                        updateVariant(index, { variantValue: e.target.value })
                      }
                      placeholder="Cream & Onion / Red / 500ml"
                    />
                  </div>
                  <div>
                    <Label>Pack Label</Label>
                    <FreeTextSelectInput
                      value={variant.packLabel}
                      onChange={(v) => updateVariant(index, { packLabel: v })}
                      placeholder="Select or type pack label"
                      listId={`pack-label-options-${index}`}
                      options={PACK_LABEL_OPTIONS}
                    />
                  </div>

                  <div>
                    <Label>Variant SKU</Label>
                    <Input
                      value={variant.variantSku}
                      onChange={(e) =>
                        updateVariant(index, { variantSku: e.target.value })
                      }
                      placeholder="SKU for this variant"
                    />
                  </div>
                  <div>
                    <Label>Variant Barcode</Label>
                    <Input
                      value={variant.barcode}
                      maxLength={15}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,15}$/.test(value)) {
                          updateVariant(index, { barcode: value });
                        }
                      }}
                      placeholder="Barcode for this variant"
                    />
                  </div>
                  <div>
                    <Label>Qty / Pack Value</Label>
                    <Input
                      value={variant.unitValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) {
                          updateVariant(index, { unitValue: value });
                        }
                      }}
                      placeholder="250 / 6 / 1"
                      inputMode="decimal"
                    />
                  </div>
                  <div>
                    <Label>Qty / Pack Unit</Label>
                    <QuickSelectInput
                      value={variant.unitSymbol}
                      onChange={(v) => updateVariant(index, { unitSymbol: v })}
                      placeholder="Select qty unit"
                      listId="qty-pack-unit-options"
                      options={QTY_PACK_UNIT_OPTIONS}
                    />
                  </div>

                  <div>
                    <Label>Weight Value</Label>
                    <Input
                      value={variant.weightValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) {
                          updateVariant(index, { weightValue: value });
                        }
                      }}
                      placeholder="250 / 1"
                      inputMode="decimal"
                    />
                  </div>
                  <div>
                    <Label>Weight Unit</Label>
                    <QuickSelectInput
                      value={variant.weightUnitSymbol}
                      onChange={(v) =>
                        updateVariant(index, {
                          weightUnitSymbol: v,
                        })
                      }
                      placeholder="Select weight unit"
                      listId="weight-unit-options"
                      options={WEIGHT_UNIT_OPTIONS}
                    />
                  </div>
                  <div>
                    <Label>MRP</Label>
                    <Input
                      value={variant.mrp}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) {
                          updateVariant(index, { mrp: value });
                        }
                      }}
                      placeholder="0"
                      inputMode="decimal"
                    />
                  </div>
                  <div>
                    <Label>Selling Price</Label>
                    <Input
                      value={variant.sellingPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) {
                          updateVariant(index, { sellingPrice: value });
                        }
                      }}
                      placeholder="0"
                      inputMode="decimal"
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div>
                    <Label>Variant Short Description</Label>
                    <Input
                      value={variant.shortDescription}
                      onChange={(e) =>
                        updateVariant(index, {
                          shortDescription: e.target.value,
                        })
                      }
                      placeholder="Short variant description"
                    />
                  </div>
                  <div>
                    <Label>Variant Active</Label>
                    <Toggle
                      value={variant.isActive}
                      onChange={(v) => updateVariant(index, { isActive: v })}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Variant Description</Label>
                  <Textarea
                    value={variant.description}
                    onChange={(e) =>
                      updateVariant(index, { description: e.target.value })
                    }
                    placeholder="Full description for this variant only"
                  />
                </div>

                <ImagePicker
                  title="Variant Images"
                  required
                  maxImages={4}
                  existingImages={variant.existingImages}
                  newImages={variant.newImages}
                  coverSelection={variant.coverSelection}
                  fileInputRef={getVariantFileRef(index)}
                  onAddFiles={(files) => addVariantFiles(index, files)}
                  onRemoveExisting={(img) =>
                    removeVariantExistingImage(index, img)
                  }
                  onRemoveNew={(imageIndex) =>
                    removeVariantNewImage(index, imageIndex)
                  }
                  onSelectCover={(next) =>
                    updateVariant(index, { coverSelection: next })
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => nav("/admin/dashboard/products")}
            className="h-[42px] rounded-[12px] border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-[42px] items-center justify-center rounded-[12px] bg-[#d86a78] px-6 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : isEdit ? "Update Product" : "Save Product"}
          </button>
        </div>
      </form>

      <CatalogManagerDrawer
        open={openCatalog}
        onClose={() => setOpenCatalog(false)}
        initialTab={catalogTab}
        onSaved={loadLookups}
      />
    </div>
  );
}
